import type React from 'react';
import { useIntl } from 'react-intl';

import { TextButton } from '@xing-com/button';
import { Player } from '@xing-com/crate-video-player';
import { IconCross, IconArrowRight } from '@xing-com/icons';
import { Headline } from '@xing-com/typography';

import type {
  generateTipsAndTricksActionUrlTrackingParamsType,
  onTipsAndTricksVideoTrackingType,
  tipsAndTricksTopBottomType,
} from '../../types';

import { Styled } from './styles';

export type TipsAndTricksProps = {
  hideButtonsFromFocus: boolean;
  videoId: number;
  top?: tipsAndTricksTopBottomType;
  bottom?: tipsAndTricksTopBottomType;
  onButtonCloseClick: () => void;
  onVideoTracking?: onTipsAndTricksVideoTrackingType;
  generateActionUrlTrackingParams?: generateTipsAndTricksActionUrlTrackingParamsType;
  tabTrackingId: string;
  groupTrackingId: string;
};

export const TipsAndTricks: React.FC<TipsAndTricksProps> = ({
  hideButtonsFromFocus,
  videoId,
  top,
  bottom,
  onButtonCloseClick,
  onVideoTracking,
  generateActionUrlTrackingParams,
  tabTrackingId,
  groupTrackingId,
}) => {
  const intl = useIntl();

  return (
    <Styled.Container>
      <Styled.UnstyledButton
        aria-label={intl.formatMessage({
          id: 'BUTTON_CLOSE',
        })}
        onClick={() => {
          onButtonCloseClick();
        }}
        data-testid={`close-${tabTrackingId}-${groupTrackingId}`}
        tabIndex={hideButtonsFromFocus ? -1 : 0}
      >
        <IconCross height={24} />
      </Styled.UnstyledButton>
      <Styled.ContentContainer>
        <Styled.VideoContainer>
          <Player
            section={groupTrackingId}
            id={videoId}
            onReady={() => ({})}
            onError={() => ({})}
            onStart={() =>
              onVideoTracking &&
              onVideoTracking('video_start', tabTrackingId, groupTrackingId)
            }
            onPause={() =>
              onVideoTracking &&
              onVideoTracking('video_pause', tabTrackingId, groupTrackingId)
            }
            onComplete={() =>
              onVideoTracking &&
              onVideoTracking('video_finish', tabTrackingId, groupTrackingId)
            }
          />
        </Styled.VideoContainer>
        <Styled.ActionsContainer>
          {top && (
            <div>
              <Headline
                size="large"
                data-testid={`tips-and-tricks-tab-${tabTrackingId}-group-${groupTrackingId}-top`}
              >
                {top.header}
              </Headline>
              {top.actions?.map((action) => (
                <Styled.TopButton
                  data-testid={`cta-${tabTrackingId}-${groupTrackingId}-${action.trackingId}`}
                  target="_parent"
                  // @ts-expect-error FIXME
                  href={
                    generateActionUrlTrackingParams
                      ? action.url +
                        generateActionUrlTrackingParams(
                          tabTrackingId,
                          groupTrackingId,
                          action.trackingId || ''
                        )
                      : action.url
                  }
                  variant="primary"
                  size="small"
                  sizeConfined="medium"
                  key={action.text}
                >
                  {action.text}
                </Styled.TopButton>
              ))}
            </div>
          )}
          {bottom && (
            <Styled.BottomContainer>
              <Styled.BottomContainerHeadline
                size="large"
                data-testid={`tips-and-tricks-tab-${tabTrackingId}-group-${groupTrackingId}-bottom`}
              >
                {bottom.header}
              </Styled.BottomContainerHeadline>
              {bottom.actions?.map((action) => (
                <TextButton
                  icon={IconArrowRight}
                  target="_parent"
                  // @ts-expect-error FIXME
                  href={
                    generateActionUrlTrackingParams
                      ? action.url +
                        generateActionUrlTrackingParams(
                          tabTrackingId,
                          groupTrackingId,
                          action.trackingId || ''
                        )
                      : action.url
                  }
                  key={action.text}
                >
                  {action.text}
                </TextButton>
              ))}
            </Styled.BottomContainer>
          )}
        </Styled.ActionsContainer>
      </Styled.ContentContainer>
    </Styled.Container>
  );
};
